"use client";

import React from "react";
import { Button, ConfigProvider } from "antd";
import type { ButtonProps } from "antd";
import { orionTheme } from "../theme/orion-theme";
import clsx from "clsx";
import {
  InboxOutlined,
  DeleteOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import "../styles/action-button.css";

export type ActionType = "archive" | "delete" | "cancel";

export interface ActionButtonProps extends Omit<ButtonProps, "icon" | "type"> {
  /**
   * The type of action button
   */
  actionType?: ActionType;

  /**
   * Additional CSS classes (supports Tailwind overrides)
   */
  className?: string;
}

/**
 * ActionButton component - Archive, Delete, Cancel buttons with OpenStore Orion theming
 *
 * This component provides action buttons based on Ant Design Button
 * with OpenStore Orion theme and Figma design specifications.
 *
 * Features:
 * - Figma design specifications (specific colors, hover states)
 * - Three action types: archive, delete, cancel
 * - Automatic theming
 * - Tailwind class override support
 * - ALL Ant Design Button features and props
 *
 * @example
 * ```tsx
 * // Archive button
 * <ActionButton actionType="archive" onClick={handleArchive} />
 *
 * // Delete button
 * <ActionButton actionType="delete" onClick={handleDelete} />
 *
 * // Cancel button
 * <ActionButton actionType="cancel" onClick={handleCancel} />
 *
 * // With custom props
 * <ActionButton
 *   actionType="archive"
 *   onClick={handleArchive}
 *   disabled={isDisabled}
 *   loading={isLoading}
 *   className="custom-action"
 * />
 * ```
 */
export const ActionButton: React.FC<ActionButtonProps> = ({
  actionType = "archive",
  className,
  children,
  ...props
}) => {
  const getIcon = (type: ActionType) => {
    switch (type) {
      case "archive":
        return <InboxOutlined />;
      case "delete":
        return <DeleteOutlined />;
      case "cancel":
        return <CloseOutlined />;
      default:
        return <InboxOutlined />;
    }
  };

  const getActionText = (type: ActionType) => {
    switch (type) {
      case "archive":
        return "Archive";
      case "delete":
        return "Delete";
      case "cancel":
        return "Cancel";
      default:
        return "Archive";
    }
  };

  return (
    <ConfigProvider theme={orionTheme}>
      <Button
        type="text"
        icon={getIcon(actionType)}
        className={clsx(
          "orion-action-button",
          `orion-action-button--${actionType}`,
          className
        )}
        {...props}
      >
        {children || getActionText(actionType)}
      </Button>
    </ConfigProvider>
  );
};

ActionButton.displayName = "ActionButton";

// Export convenience components
export const ArchiveButton: React.FC<Omit<ActionButtonProps, "actionType">> = (
  props
) => <ActionButton actionType="archive" {...props} />;

export const DeleteButton: React.FC<Omit<ActionButtonProps, "actionType">> = (
  props
) => <ActionButton actionType="delete" {...props} />;

export const CancelButton: React.FC<Omit<ActionButtonProps, "actionType">> = (
  props
) => <ActionButton actionType="cancel" {...props} />;
